import 'swiper/css'

import styles from './carousel.module.css'

import cn from 'clsx'
import type { ReactNode } from 'react'
import React from 'react'
import { Mousewheel, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { Box } from 'components/ds'
import Icon from 'components/ui/icon'

type CarouselBreakpoint = {
  slidesPerView: number
  slidesPerGroup?: number
  spaceBetween?: number
}

type CarouselProps = {
  children: ReactNode
  slidesPerView?: number | 'auto'
  spaceBetween?: number
  navKey: string
  className?: string
  breakpoints?: Record<number, CarouselBreakpoint>
  loading?: boolean
  loadingSkeleton?: ReactNode
  /**
   * When true, uses CSS container queries for responsive slide widths
   * based on container width instead of viewport width.
   */
  useContainerBreakpoints?: boolean
}

const Carousel = ({
  children,
  slidesPerView = 'auto',
  spaceBetween = 12,
  navKey,
  className,
  breakpoints,
  loading = false,
  loadingSkeleton,
  useContainerBreakpoints = false,
}: CarouselProps): React.ReactNode => {
  const containerClassName = cn(
    styles.container,
    useContainerBreakpoints && styles.containerBreakpoints,
    className,
  )

  if (loading && loadingSkeleton) {
    return <Box className={containerClassName}>{loadingSkeleton}</Box>
  }

  return (
    <Box className={containerClassName}>
      <button
        id={`carousel-prev-${navKey}`}
        className={cn(styles.navButton, styles.navButtonPrev)}
        aria-label="Previous slide"
      >
        <Icon type="arrow-left" />
      </button>

      <button
        id={`carousel-next-${navKey}`}
        className={cn(styles.navButton, styles.navButtonNext)}
        aria-label="Next slide"
      >
        <Icon type="arrow-right" />
      </button>

      <Swiper
        modules={[Navigation, Mousewheel]}
        spaceBetween={spaceBetween}
        slidesPerView={slidesPerView}
        navigation={{
          prevEl: `#carousel-prev-${navKey}`,
          nextEl: `#carousel-next-${navKey}`,
        }}
        mousewheel={{
          forceToAxis: true,
          releaseOnEdges: false,
        }}
        edgeSwipeDetection={true}
        edgeSwipeThreshold={20}
        touchEventsTarget="container"
        breakpoints={breakpoints}
        threshold={2}
        noSwiping
        noSwipingClass="swiper-no-swiping"
      >
        {React.Children.map(children, (child, index) => (
          <SwiperSlide className={styles.slide} key={index}>
            {child}
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  )
}

export default Carousel
